import React from 'react';
import { Youtube } from 'lucide-react';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get all images from a shrimp
export const getAllImages = (shrimp) => {
  const images = [];
  if (shrimp.image_url) images.push(shrimp.image_url);
  if (shrimp.images && shrimp.images.length > 0) {
    shrimp.images.forEach(img => {
      if (img && !images.includes(img)) images.push(img);
    });
  }
  return images;
};

export const getPrimaryImage = (shrimp) => {
  const images = getAllImages(shrimp);
  return images.length > 0 ? images[0] : null;
};

// Calculate discounted price - considers item discount and store-wide discount
export const getDisplayPrice = (shrimp, settings = null, scheduledDiscount = null) => {
  if (!shrimp.price) return null;
  
  // If item has its own discount, use that
  if (shrimp.discount_percent && shrimp.discount_percent > 0) {
    return shrimp.price * (1 - shrimp.discount_percent / 100);
  }
  
  // Check scheduled discount first (new system)
  if (scheduledDiscount?.is_enabled && scheduledDiscount?.discount_percent > 0 && !shrimp.exclude_from_store_discount) {
    return shrimp.price * (1 - scheduledDiscount.discount_percent / 100);
  }
  
  // Apply store-wide discount if enabled and item not excluded (old settings system)
  if (settings && settings.store_discount_enabled && settings.store_discount_percent > 0) {
    // Check if discount is within date range (if dates are set)
    const now = new Date();
    if (settings.store_discount_start_date) {
      const startDate = new Date(settings.store_discount_start_date);
      if (now < startDate) return shrimp.price; // Discount hasn't started yet
    }
    if (settings.store_discount_end_date) {
      const endDate = new Date(settings.store_discount_end_date);
      endDate.setHours(23, 59, 59, 999); // End of day
      if (now > endDate) return shrimp.price; // Discount has ended
    }
    
    // Apply discount if item not excluded
    if (!shrimp.exclude_from_store_discount) {
      return shrimp.price * (1 - settings.store_discount_percent / 100);
    }
  }
  
  return shrimp.price;
};

// Get effective discount info for display
export const getEffectiveDiscount = (shrimp, settings = null, scheduledDiscount = null) => {
  // If item has its own discount, use that
  if (shrimp.discount_percent && shrimp.discount_percent > 0) {
    return { percent: shrimp.discount_percent, name: shrimp.discount_name, isStoreWide: false };
  }
  
  // Check scheduled discount first (new system)
  if (scheduledDiscount?.is_enabled && scheduledDiscount?.discount_percent > 0 && !shrimp.exclude_from_store_discount) {
    return { 
      percent: scheduledDiscount.discount_percent, 
      name: scheduledDiscount.name || 'Store Sale', 
      isStoreWide: true 
    };
  }
  
  // Check store-wide discount (old settings system)
  if (settings && settings.store_discount_enabled && settings.store_discount_percent > 0 && !shrimp.exclude_from_store_discount) {
    const now = new Date();
    if (settings.store_discount_start_date) {
      const startDate = new Date(settings.store_discount_start_date);
      if (now < startDate) return null;
    }
    if (settings.store_discount_end_date) {
      const endDate = new Date(settings.store_discount_end_date);
      endDate.setHours(23, 59, 59, 999);
      if (now > endDate) return null;
    }
    return { percent: settings.store_discount_percent, name: 'Store Sale', isStoreWide: true };
  }
  
  return null;
};

// ---- Pack pricing helpers -------------------------------------------------
// Reuses the two-slot grade structure. slot: 'high' = pack 1 (price /
// discount_percent), 'mid' = pack 2 (price_mid_grade / discount_percent_mid).
export const optionLabel = (s, slot) => {
  if (s?.pack_mode) {
    const size = slot === 'mid' ? s.pack_size_2 : s.pack_size_1;
    return (size !== null && size !== undefined && size !== '') ? `${size} Pack` : 'Pack';
  }
  return slot === 'mid' ? 'Mid Grade' : 'High Grade';
};

export const optionSelectHeading = (s) => (s?.pack_mode ? 'Select Pack Size *' : 'Select Grade *');

export const optionDesc = (s, slot) => {
  if (s?.pack_mode) return '';
  return slot === 'mid'
    ? 'Good quality, some variation in color/pattern'
    : 'Best quality, vibrant color/pattern';
};

// Returns a product clone whose price/discount reflect the chosen slot so the
// existing getDisplayPrice/getEffectiveDiscount work unchanged for grades & packs.
export const optionShrimp = (s, slot) => {
  if (!s) return s;
  const price = slot === 'mid' ? s.price_mid_grade : s.price;
  const discount_percent = (s.pack_mode && slot === 'mid') ? s.discount_percent_mid : s.discount_percent;
  return {
    ...s,
    price,
    discount_percent,
    exclude_from_store_discount: s.pack_mode ? true : s.exclude_from_store_discount,
  };
};

// Cart line display name incl. the pack / grade suffix.
export const buildCartName = (s, slot) => {
  if (!s) return '';
  if (s.pack_mode) {
    if (s.grades_enabled) return `${s.name} (${optionLabel(s, slot || 'high')})`;
    return `${s.name} (${optionLabel(s, 'high')})`;
  }
  if (s.grades_enabled) return `${s.name} (${slot === 'high' ? 'High Grade' : 'Mid Grade'})`;
  return s.name;
};

// Label for a single-pack product (no selector shown), else null.
export const singlePackLabel = (s) => (s?.pack_mode && !s?.grades_enabled ? optionLabel(s, 'high') : null);

// Extract YouTube video ID from various URL formats
export const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\?\/]+)/,
    /youtube\.com\/shorts\/([^&\?\/]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// YouTube embed component
export const YouTubeEmbed = ({ url }) => {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) return null;
  return (
    <div className="aspect-video rounded-lg overflow-hidden bg-black">
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        title="YouTube video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
      />
    </div>
  );
};
